import type { LocalSubRegionItem } from '@/api/room/types'
import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  convert_region_r2c,
  rotateRect_cw,
  rotateRect_ccw,
  convert_region_c2r,
} from '@/utils/coordTransform'
import { useRoomStore } from '@/stores/room'

export const useAlarmStore = defineStore(
  'alarm',
  () => {
    const alarmRegionItem = {
      nanoid: nanoid(),
      startXx: 0,
      stopXx: 100,
      startYy: 0,
      stopYy: 100,
      startZz: 0,
      stopZz: 0,
      pixelX: 250,
      pixelY: 250,
      pixelWidth: 100,
      pixelHeight: 100,
      isDraging: false,
      isResizing: false,
      isActive: false,
      isTracking: false,
      isFalling: false,
      isBed: false,
      isLowSnr: 0,
      isDoor: 0,
      presenceEnterDuration: 3,
      presenceExitDuration: 3,
      trackPresence: 0,
      excludeFalling: 0,
    }

    const roomStore = useRoomStore()
    const alarmRegions = ref<LocalSubRegionItem[]>([{ ...alarmRegionItem }])

    const initAlarmRegions = () => {
      alarmRegions.value = [{ ...alarmRegionItem }]
    }

    const handlerRegionR2C = (item: LocalSubRegionItem, angle: number = roomStore.radarAngle) => {
      const regionItem = convert_region_r2c(
        {
          x_cm_start: Number(item.startXx),
          x_cm_stop: Number(item.stopXx),
          y_cm_start: Number(item.startYy),
          y_cm_stop: Number(item.stopYy),
        },
        {
          x_radar: 250,
          y_radar: 250,
        }
      )

      const routeRegion = rotateRect_cw(regionItem, { x: 250, y: 250 }, angle)

      alarmRegions.value[0] = {
        ...alarmRegions.value[0],
        pixelX: routeRegion.left,
        pixelY: routeRegion.top,
        pixelWidth: routeRegion.width,
        pixelHeight: routeRegion.height,
      }

      return {
        ...item,
        pixelX: routeRegion.left,
        pixelY: routeRegion.top,
        pixelWidth: routeRegion.width,
        pixelHeight: routeRegion.height,
      }
    }

    const handlerRegionC2R = (item: LocalSubRegionItem, angle: number = roomStore.radarAngle) => {
      const routeRegion = rotateRect_ccw(
        {
          left: item.pixelX,
          top: item.pixelY,
          width: item.pixelWidth,
          height: item.pixelHeight,
        },
        { x: 250, y: 250 },
        angle
      )

      const regionItem = convert_region_c2r(
        {
          left: routeRegion.left,
          top: routeRegion.top,
          width: routeRegion.width,
          height: routeRegion.height,
        },
        {
          x_radar: 250,
          y_radar: 250,
        }
      )

      alarmRegions.value[0] = {
        ...alarmRegions.value[0],
        startXx: Math.round(regionItem.x_cm_start),
        stopXx: Math.round(regionItem.x_cm_stop),
        startYy: Math.round(regionItem.y_cm_start),
        stopYy: Math.round(regionItem.y_cm_stop),
      }

      return {
        ...item,
        startXx: Math.round(regionItem.x_cm_start),
        stopXx: Math.round(regionItem.x_cm_stop),
        startYy: Math.round(regionItem.y_cm_start),
        stopYy: Math.round(regionItem.y_cm_stop),
      }
    }

    return {
      alarmRegionItem,
      alarmRegions,
      initAlarmRegions,
      handlerRegionR2C,
      handlerRegionC2R,
    }
  },
  {
    persist: {
      key: `${import.meta.env.VITE_STORE_NAME}_ALARM_INFO`,
      storage: localStorage,
    },
  }
)
